import { Controller } from "@hotwired/stimulus"
import { formatPln, getAllJSON, getJSON, listResults } from "../api.js"

// Katalog (panel klienta): wiersze tabeli + sidebar kategorii + filtry z DRF.
//
// Paginacja/filtry są SERVER-SIDE: stan żyje w parametrach URL (?subcategory=&voivodeship=
// &lead_type=&page=), a kontroler przekazuje je do /api/catalog/ i renderuje wynik.
// Linki paginacji/sidebara to zwykłe hrefy (przeładowanie strony) — progressive enhancement.
// Serializer katalogu jest 0-PII; wypełniamy wyłącznie textContent (bez innerHTML).
export default class extends Controller {
  static targets = ["title", "count", "rows", "status", "pagination", "pageInfo", "pageLinks",
                    "catlist", "voivFilter", "countyFilter", "typeFilter", "sortFilter", "dirFilter",
                    "subcatInput"]
  static values = {
    url: String,               // /api/catalog/
    categoriesUrl: String,     // /api/categories/
    subcategoriesUrl: String,  // /api/subcategories/
    voivodeshipsUrl: String,   // /api/voivodeships/
    countiesUrl: String,       // /api/counties/
    noOrg: Boolean,            // true = user bez aktywnej organizacji (katalog niedostępny)
  }

  static PAGE_SIZE = 25
  static FILTER_KEYS = ["subcategory", "voivodeship", "county", "lead_type", "sort", "dir"]

  connect() {
    this.params = new URLSearchParams(window.location.search)
    this.#initFilters()
    this.#loadSidebar()
    this.#loadVoivodeships()
    this.#loadCounties()
    this.#loadLeads()
  }

  // --- prywatne ---

  #initFilters() {
    if (this.hasSubcatInputTarget) this.subcatInputTarget.value = this.params.get("subcategory") || ""
    if (this.hasTypeFilterTarget)  this.typeFilterTarget.value  = this.params.get("lead_type") || ""
    if (this.hasSortFilterTarget)  this.sortFilterTarget.value  = this.params.get("sort") || "fresh"
    if (this.hasDirFilterTarget)   this.dirFilterTarget.value   = this.params.get("dir") || ""
  }

  async #loadLeads() {
    if (this.noOrgValue) {
      this.#status("Dołącz do organizacji, aby przeglądać i kupować leady.")
      if (this.hasCountTarget) this.countTarget.textContent = "0"
      return
    }
    const query = new URLSearchParams()
    for (const key of [...this.constructor.FILTER_KEYS, "page"]) {
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    let data
    try {
      data = await getJSON(query.toString() ? `${this.urlValue}?${query}` : this.urlValue)
    } catch {
      this.#status("Nie udało się załadować leadów. Odśwież stronę.")
      return
    }
    this.#renderRows(listResults(data))
    if (this.hasCountTarget) this.countTarget.textContent = data.count ?? "0"
    this.#renderPagination(data)
  }

  #renderRows(items) {
    this.rowsTarget.textContent = ""
    if (!items.length) {
      this.#status("Brak leadów spełniających kryteria.")
      return
    }
    this.#status(null)
    const template = document.getElementById("catalog-row-template")
    for (const item of items) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.dataset.leadId = item.id
      row.classList.toggle("is-selected", item.in_cart)

      // Checkbox = zaznaczenie do bulk-add (stan UŻYTKOWNIKA) — celowo NIE pre-zaznaczamy
      // pozycji będących w koszyku, inaczej bulkbar liczyłby je jako "wybrane do dodania".
      const checkbox = row.querySelector("input.ls-checkbox")
      checkbox.value = item.id

      row.querySelector('[data-field="cat"]').textContent = item.category_path
      row.querySelector('[data-field="code"]').textContent = item.code
      row.querySelector('[data-field="note"]').textContent = item.note_preview
      row.querySelector('[data-field="region"]').textContent = item.region
      row.querySelector('[data-field="fresh"]').textContent = item.fresh_label

      const badge = row.querySelector('[data-field="badge"]')
      const kind = item.score === "hot" || item.score === "warm" ? item.score : "new"
      badge.className = `ls-badge ls-badge--${kind}`
      badge.textContent = item.score_label

      const price = formatPln(item.price_pln)
      row.querySelector('[data-field="price"]').textContent = price

      const previewBtn = row.querySelector("[data-action='preview#open']")
      Object.assign(previewBtn.dataset, {
        previewId: item.id,
        previewCode: item.code,
        previewCat: item.category_path,
        previewRegion: item.region,
        previewFresh: item.fresh_label,
        previewScore: item.score_label,
        previewScoreKind: kind,
        previewNote: item.note,
        previewPrice: price,
      })

      const addBtn = row.querySelector(".ls-add-btn")
      addBtn.setAttribute("data-cart-id-param", item.id)
      if (item.in_cart) {
        addBtn.classList.add("is-in-cart")
        addBtn.querySelector('[data-field="addIcon"]').textContent = "✓"
        addBtn.querySelector("[data-cart-label]").textContent = "W koszyku"
      }
      this.rowsTarget.appendChild(row)
    }
  }

  #renderPagination(data) {
    if (!this.hasPaginationTarget) return
    const hasPages = Boolean(data.next || data.previous)
    this.paginationTarget.hidden = !hasPages
    if (!hasPages) return

    const page = Number(this.params.get("page") || 1)
    const shown = listResults(data).length
    const start = (page - 1) * this.constructor.PAGE_SIZE + 1
    this.pageInfoTarget.textContent = `${start}–${start + shown - 1} z ${data.count}`

    this.pageLinksTarget.textContent = ""
    const makeLink = (label, targetPage, enabled) => {
      const a = document.createElement("a")
      a.className = `ls-page${enabled ? "" : " is-disabled"}`
      a.textContent = label
      if (enabled) a.href = this.#pageHref(targetPage)
      this.pageLinksTarget.appendChild(a)
    }
    makeLink("← Poprzednia", page - 1, Boolean(data.previous))
    const current = document.createElement("span")
    current.className = "ls-page is-active"
    current.textContent = page
    this.pageLinksTarget.appendChild(current)
    makeLink("Następna →", page + 1, Boolean(data.next))
  }

  #pageHref(page) {
    const query = new URLSearchParams()
    for (const key of this.constructor.FILTER_KEYS) {
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    if (page > 1) query.set("page", page)
    const text = query.toString()
    return text ? `?${text}` : window.location.pathname
  }

  async #loadSidebar() {
    if (!this.hasCatlistTarget) return
    let categories
    let subcategories
    try {
      ;[categories, subcategories] = await Promise.all([
        getAllJSON(this.categoriesUrlValue),
        getAllJSON(this.subcategoriesUrlValue),
      ])
    } catch {
      return // sidebar zostaje z samym "Wszystkie leady"
    }
    const active = this.params.get("subcategory") || ""
    if (!active) this.catlistTarget.querySelector(".ls-catlink")?.classList.add("is-active")
    else this.catlistTarget.querySelector(".ls-catlink")?.classList.remove("is-active")

    const subsByCategory = new Map()
    for (const sub of subcategories) {
      const list = subsByCategory.get(sub.category) ?? []
      list.push(sub)
      subsByCategory.set(sub.category, list)
      // Tytuł strony = nazwa aktywnej podkategorii (zamiast "Wszystkie leady").
      if (active && sub.slug === active && this.hasTitleTarget) this.titleTarget.textContent = sub.name
    }
    for (const category of categories) {
      const subs = subsByCategory.get(category.id) ?? []
      // Kategoria z aktywną podkategorią startuje rozwinięta; reszta zwinięta.
      const expanded = subs.some((sub) => sub.slug === active)

      const group = document.createElement("div")
      group.className = "ls-cat-group"

      // Nagłówek kategorii = przycisk-toggle (NIE nawiguje) z caretem ▸/▾.
      const toggle = document.createElement("button")
      toggle.type = "button"
      toggle.className = "ls-catlink ls-cat-toggle"
      toggle.setAttribute("data-action", "catalog#toggleCategory")
      toggle.setAttribute("aria-expanded", String(expanded))
      const name = document.createElement("span")
      name.textContent = category.name
      const caret = document.createElement("span")
      caret.className = "ls-cat-caret"
      caret.textContent = expanded ? "▾" : "▸"
      toggle.append(name, caret)
      group.appendChild(toggle)

      // Kontener zwijany z podkategoriami (linki server-side jak dotąd).
      const subsBox = document.createElement("div")
      subsBox.className = "ls-cat-subs"
      subsBox.hidden = !expanded
      for (const sub of subs) {
        const link = document.createElement("a")
        link.className = `ls-catlink ls-catlink--sub${active === sub.slug ? " is-active" : ""}`
        link.href = this.#subcategoryHref(sub.slug)
        link.textContent = sub.name
        subsBox.appendChild(link)
      }
      group.appendChild(subsBox)
      this.catlistTarget.appendChild(group)
    }
  }

  // Accordion: klik w nagłówek kategorii rozwija/zwija jej podkategorie (bez nawigacji).
  toggleCategory(event) {
    const toggle = event.currentTarget
    const group = toggle.closest(".ls-cat-group")
    const subsBox = group.querySelector(".ls-cat-subs")
    const caret = toggle.querySelector(".ls-cat-caret")
    const willExpand = subsBox.hidden
    subsBox.hidden = !willExpand
    toggle.setAttribute("aria-expanded", String(willExpand))
    if (caret) caret.textContent = willExpand ? "▾" : "▸"
  }

  // Link podkategorii zachowuje pozostałe filtry (spójnie z paginacją); zeruje stronę.
  #subcategoryHref(slug) {
    const query = new URLSearchParams()
    for (const key of this.constructor.FILTER_KEYS) {
      if (key === "subcategory") continue
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    query.set("subcategory", slug)
    return `?${query}`
  }

  async #loadVoivodeships() {
    if (!this.hasVoivFilterTarget) return
    let voivodeships
    try {
      voivodeships = await getAllJSON(this.voivodeshipsUrlValue)
    } catch {
      return
    }
    const active = this.params.get("voivodeship") || ""
    for (const voivodeship of voivodeships) {
      const option = document.createElement("option")
      option.value = voivodeship.id
      option.textContent = voivodeship.name
      this.voivFilterTarget.appendChild(option)
    }
    this.voivFilterTarget.value = active
  }

  async #loadCounties() {
    if (!this.hasCountyFilterTarget) return
    // Powiat zależy od województwa: bez wybranego województwa select jest wyłączony.
    const voivodeship = this.params.get("voivodeship") || ""
    if (!voivodeship) {
      this.countyFilterTarget.disabled = true
      return
    }
    let counties
    try {
      counties = await getAllJSON(`${this.countiesUrlValue}?voivodeship=${voivodeship}`)
    } catch {
      return
    }
    for (const county of counties) {
      const option = document.createElement("option")
      option.value = county.id
      option.textContent = county.name
      this.countyFilterTarget.appendChild(option)
    }
    this.countyFilterTarget.value = this.params.get("county") || ""
    this.countyFilterTarget.disabled = false
  }

  #status(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
